import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyScanUserDetail } from './companyScanApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  toneChip
} from '../../../../component/common/designSystem';
import { SCAN_COLORS, STATUS_TONES, STATUS_TEXT, scanColor } from './scanTheme';

/**
 * Scans → User profile.
 *
 * One person's scan history: how many of each scan they took, when they
 * started, when they last scanned, and whether the analysis finished each time.
 *
 * THERE IS NO RESULT ON THIS PAGE, and there is not going to be one. Not a skin
 * score or risk level, not a hygiene score or band, not a facial age, not a
 * wellbeing reading, not a photo and not a report link. Every one of those is a
 * health finding about the person whose name is at the top of this page, and it
 * was addressed to them, not to their employer. The endpoint behind this page
 * does not read any of it, so there is nothing here to leak.
 *
 * The history list exists for one question an admin can legitimately have —
 * "her scans keep failing, is the app broken for her?" — and answers it with a
 * date and a status, which answers nothing about her.
 *
 * `fetcher`/`backPath`/`backLabel` are props so an unscoped hub could reuse the
 * page unchanged.
 */
const ScanUserDetail = ({ fetcher = fetchCompanyScanUserDetail, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const scans = detail?.scans || [];
  const scanMix = detail?.scanMix || [];
  const history = detail?.history || [];
  const scansTrend = (detail?.dailyTrend || []).map((day) => day.scans || 0);

  const usedScans = scans.filter((row) => row.scans > 0);
  const trendHasData = scansTrend.some((value) => value > 0);

  const failed = scans.reduce((sum, row) => sum + (row.failed || 0), 0);

  const scanMixColors = scanMix.map((row) => {
    const match = scans.find((s) => s.label === row.label);
    return scanColor(match?.key);
  });

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(scansTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} scans` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.rating}
          title="Scans Taken"
          value={user ? formatBigNumber(user.totalScans) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Scan Types Used"
          value={user ? `${user.typesUsed} of ${user.typesAvailable}` : '-'}
        />
        {/* A support signal, not a health one: repeated failures mean the app
            is not working for this person. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Failed Analyses"
          value={detail ? formatBigNumber(failed) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.newUsers}
          title="First Scan"
          value={user?.firstScanDate || '-'}
        />
        <StatCard icon={STAT_ICONS.sessions} title="Last Scan" value={user?.lastScanDate || '-'} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <MiniDonutCard
          title="Which Scans They Take"
          subtitle="Scans by type"
          data={scanMix}
          colors={scanMixColors}
          unit="scans"
          totalLabel="Scans"
          legendLimit={3}
        />

        <Panel
          className="xl:col-span-2"
          title="Scanning Over Time"
          subtitle="Scans across all three types — last 30 days"
        >
          {trendHasData ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Scans', data: scansTrend }]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No scans in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="mt-6">
        <Panel title="Scan by Scan" subtitle="How much they have used each one, and how it went">
          {usedScans.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Scan</th>
                  <th className="py-2 px-4 font-medium text-center">Taken</th>
                  <th className="py-2 px-4 font-medium text-center">Analysed</th>
                  <th className="py-2 px-4 font-medium text-center">Failed</th>
                  <th className="py-2 px-4 font-medium text-center">First</th>
                  <th className="py-2 font-medium text-right">Last</th>
                </>
              }
            >
              {usedScans.map((row) => (
                <tr key={row.key} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: SCAN_COLORS[row.key] }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.scans)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.done)}</td>
                  <td className="py-2 px-4 text-center">
                    {row.failed > 0 ? (
                      <span className="text-red-500">{formatBigNumber(row.failed)}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="py-2 px-4 text-center">{row.firstDate || '-'}</td>
                  <td className="py-2 text-right">{row.lastDate || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This user has not taken a scan yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="mt-6">
        <Panel
          title="Recent Scans"
          subtitle={`The last ${
            detail?.historyLimit || 20
          } — when, and whether the analysis finished`}
        >
          {history.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium">Scan</th>
                  <th className="py-2 px-4 font-medium text-center">Outcome</th>
                  <th className="py-2 font-medium text-right">Report issued</th>
                </>
              }
            >
              {history.map((row, index) => (
                <tr key={`${row.scan}-${row.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.date}</td>
                  <td className="py-2 px-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: SCAN_COLORS[row.scan] }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">
                    <Chip className={toneChip(STATUS_TONES[row.status] || 'neutral')}>
                      {STATUS_TEXT[row.status] || row.status}
                    </Chip>
                  </td>
                  {/* Whether a report exists — never a link to it. The report
                      is the person's own medical document. */}
                  <td className="py-2 text-right">{row.hasReport ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This user has not taken a scan yet.</PanelEmpty>
          )}
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            Dates and outcomes only. What each scan found went to {user?.name || 'this person'} and
            stays with them.
          </p>
        </Panel>
      </div>
    </div>
  );
};

ScanUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

export default ScanUserDetail;
